/* ============================================================================
   Kunu front-end interactions — vanilla JS port of the site-export React bits
   (sticky nav, mobile drawer, FAQ accordion, PDP gallery, qty stepper, etc.).
   All behaviour is delegated from document so it works for sections rendered
   late by the Shopify section rendering API too.
   ============================================================================ */
(function () {
  'use strict';

  // ── sticky nav scroll state ──────────────────────────────────────────────
  // We track "viewport scrolled" separately from the mobile-menu-open flag
  // so closing the menu doesn't strip the scrolled style when the user
  // has scrolled the page. The visible `.knav--scrolled` class is the OR
  // of those two inputs (mirrors the React component's logic).
  function bindStickyNav() {
    const nav = document.querySelector('.knav');
    if (!nav) return;
    const sync = () => {
      const scrolled = window.scrollY > 24 || nav.classList.contains('knav--open');
      nav.classList.toggle('knav--scrolled', scrolled);
    };
    nav._kunuSyncScroll = sync;
    sync();
    window.addEventListener('scroll', sync, { passive: true });
  }

  // ── mobile menu toggle ──────────────────────────────────────────────────
  function bindMobileMenu() {
    document.addEventListener('click', (e) => {
      const burger = e.target.closest('[data-kunu-burger]');
      if (!burger) return;
      const nav = burger.closest('.knav');
      if (!nav) return;
      const open = nav.classList.toggle('knav--open');
      burger.setAttribute('aria-expanded', String(open));
      const icon = burger.querySelector('i');
      if (icon) {
        icon.classList.toggle('fa-bars', !open);
        icon.classList.toggle('fa-xmark', open);
      }
      if (typeof nav._kunuSyncScroll === 'function') nav._kunuSyncScroll();
    });
  }

  // ── FAQ accordion ───────────────────────────────────────────────────────
  function bindFaq() {
    document.addEventListener('click', (e) => {
      const trigger = e.target.closest('.kfaq__trigger');
      if (!trigger) return;
      const list = trigger.closest('.kfaq__list');
      if (!list) return;
      const item = trigger.closest('.kfaq__item');
      const wasOpen = item.dataset.open === 'true';
      list.querySelectorAll('.kfaq__item').forEach((li) => {
        li.dataset.open = 'false';
        const t = li.querySelector('.kfaq__trigger');
        if (t) t.setAttribute('aria-expanded', 'false');
        const ic = li.querySelector('.kfaq__icon');
        if (ic) { ic.classList.add('fa-chevron-down'); ic.classList.remove('fa-chevron-up'); }
        const panel = li.querySelector('.kfaq__panel');
        if (panel) panel.style.maxHeight = '0px';
      });
      if (!wasOpen) {
        item.dataset.open = 'true';
        trigger.setAttribute('aria-expanded', 'true');
        const ic = trigger.querySelector('.kfaq__icon');
        if (ic) { ic.classList.remove('fa-chevron-down'); ic.classList.add('fa-chevron-up'); }
        const panel = item.querySelector('.kfaq__panel');
        if (panel) panel.style.maxHeight = panel.scrollHeight + 'px';
      }
    });
  }

  // ── Reserve worst-case FAQ list height so background doesn't shift ──────
  function measureFaqHeights() {
    document.querySelectorAll('.kfaq__list').forEach((ul) => {
      const rows = [...ul.querySelectorAll('.kfaq__item')];
      const gap = parseFloat(getComputedStyle(ul).rowGap) || 0;
      let triggers = 0;
      const answers = [];
      rows.forEach((li) => {
        const t = li.querySelector('.kfaq__trigger');
        if (t) triggers += t.offsetHeight;
        const a = li.querySelector('.kfaq__answer');
        if (a) answers.push(a.offsetHeight);
      });
      answers.sort((a, b) => b - a);
      const reserve = (answers[0] || 0) + (answers[1] || 0);
      ul.style.minHeight = Math.round((triggers + gap * Math.max(0, rows.length - 1) + reserve) * 0.93) + 'px';
    });
  }

  // ── PDP image gallery ───────────────────────────────────────────────────
  function bindGallery() {
    document.addEventListener('click', (e) => {
      const thumb = e.target.closest('.pdp-gallery__thumb');
      if (thumb) {
        const gallery = thumb.closest('.pdp-gallery');
        if (!gallery) return;
        const idx = parseInt(thumb.dataset.index, 10);
        const all = gallery.querySelectorAll('.pdp-gallery__thumb');
        all.forEach((t, i) => {
          t.classList.toggle('is-active', i === idx);
          t.setAttribute('aria-selected', String(i === idx));
        });
        const stage = gallery.querySelector('.pdp-gallery__main');
        const stageImg = stage && stage.querySelector('img');
        const counter = gallery.querySelector('.pdp-gallery__counter');
        const src = thumb.dataset.src;
        const fit = thumb.dataset.fit || 'cover';
        if (stage) {
          stage.classList.remove('pdp-gallery__main--cover', 'pdp-gallery__main--contain', 'is-zoom');
          stage.classList.add('pdp-gallery__main--' + fit);
          stage.style.background = fit === 'contain' ? 'var(--surface-1)' : '';
        }
        if (stageImg && src) { stageImg.src = src; stageImg.alt = thumb.querySelector('img')?.alt || ''; }
        if (counter) counter.textContent = (idx + 1) + ' / ' + all.length;
        return;
      }
      const stage = e.target.closest('.pdp-gallery__main');
      if (stage) stage.classList.toggle('is-zoom');
    });
  }

  // ── PDP collapsibles ────────────────────────────────────────────────────
  function bindPdpCollapse() {
    document.addEventListener('click', (e) => {
      const trigger = e.target.closest('.pdp-collapse__trigger');
      if (!trigger) return;
      const row = trigger.closest('.pdp-collapse__row');
      const wrap = trigger.closest('.pdp-collapse');
      if (!row || !wrap) return;
      const wasOpen = row.dataset.open === 'true';
      wrap.querySelectorAll('.pdp-collapse__row').forEach((r) => {
        r.dataset.open = 'false';
        const t = r.querySelector('.pdp-collapse__trigger');
        if (t) t.setAttribute('aria-expanded', 'false');
        const ch = r.querySelector('.pdp-collapse__chev');
        if (ch) { ch.classList.add('fa-chevron-down'); ch.classList.remove('fa-chevron-up'); }
        const p = r.querySelector('.pdp-collapse__panel');
        if (p) p.style.maxHeight = '0px';
      });
      if (!wasOpen) {
        row.dataset.open = 'true';
        trigger.setAttribute('aria-expanded', 'true');
        const ch = trigger.querySelector('.pdp-collapse__chev');
        if (ch) { ch.classList.remove('fa-chevron-down'); ch.classList.add('fa-chevron-up'); }
        const p = row.querySelector('.pdp-collapse__panel');
        if (p) p.style.maxHeight = p.scrollHeight + 'px';
      }
    });
  }

  // ── Quantity stepper ────────────────────────────────────────────────────
  function bindQty() {
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('.pdp-qty button');
      if (!btn) return;
      const wrap = btn.closest('.pdp-qty');
      const input = wrap.querySelector('input');
      let val = parseInt(input.value, 10) || 1;
      val = btn.matches('[data-qty="dec"]') ? Math.max(1, val - 1) : val + 1;
      input.value = String(val);
    });
  }

  // ── PDP Add-to-cart via Shopify Ajax API ────────────────────────────────
  function bindAddToCart() {
    document.addEventListener('submit', (e) => {
      const form = e.target.closest('form[data-kunu-add-to-cart]');
      if (!form) return;
      e.preventDefault();
      // The clicked button decides one-time vs subscription. The Subscribe &
      // Save button carries data-selling-plan (a Shopify selling-plan id); the
      // plain Add to Cart button has none. e.submitter is the activating button.
      const btn = (e.submitter && form.contains(e.submitter)) ? e.submitter : form.querySelector('.pdp-info__add');
      const label = btn && btn.querySelector('.pdp-info__add-label, .pdp-info__subscribe-label');
      const icon = btn && btn.querySelector('i');
      const originalLabel = label ? label.textContent : '';
      const originalIconClass = icon ? icon.getAttribute('class') : '';
      if (btn) { btn.disabled = true; btn.classList.add('is-loading'); }

      const body = new FormData(form);
      // Attach the selling plan only when subscribing; never POST an empty one.
      const sellingPlan = btn && btn.dataset ? btn.dataset.sellingPlan : '';
      if (sellingPlan) body.set('selling_plan', sellingPlan);
      else body.delete('selling_plan');
      // sections to refresh after add
      body.append('sections', 'cart-drawer,cart-icon-bubble');

      fetch((window.routes && window.routes.cart_add_url) || '/cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/javascript' },
        body
      })
        .then((r) => r.json())
        .then((data) => {
          if (data.status) throw data;
          if (btn) {
            if (label) label.textContent = 'Added to Cart';
            if (icon) icon.setAttribute('class', 'fa-solid fa-check');
            setTimeout(() => {
              if (label) label.textContent = originalLabel;
              if (icon) icon.setAttribute('class', originalIconClass);
            }, 2200);
          }

          // Refresh + open the drawer via Dawn's canonical renderer. It swaps
          // #CartDrawer's innerHTML and #cart-icon-bubble from the returned
          // sections, then opens. (The old applySection() swapped
          // #shopify-section-cart-drawer, but the drawer is included with
          // {% render 'cart-drawer' %} — no such wrapper exists in the DOM — so
          // the drawer never repopulated and opened empty until a page refresh.)
          const drawer = document.querySelector('cart-drawer');
          if (drawer) {
            drawer.classList.remove('is-empty');
            if (typeof drawer.setActiveElement === 'function') drawer.setActiveElement(btn);
          }

          if (drawer && typeof drawer.renderContents === 'function') {
            drawer.renderContents(data);
          } else if (drawer) {
            // Fallback if the custom element hasn't upgraded yet.
            if (data.sections) applySection('cart-icon-bubble', data.sections['cart-icon-bubble']);
            drawer.classList.add('active');
            document.body.classList.add('overflow-hidden');
          }
        })
        .catch((err) => {
          console.error('[kunu] add-to-cart failed', err);
          if (btn) { btn.disabled = false; btn.classList.remove('is-loading'); }
          alert(err && err.description ? err.description : 'Sorry, we couldn’t add this item.');
        })
        .finally(() => {
          if (btn) { btn.disabled = false; btn.classList.remove('is-loading'); }
        });
    });
  }

  function applySection(id, html) {
    if (!html) return;
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    // Look for the canonical element by id within returned html
    const next = tmp.querySelector('#shopify-section-' + id) || tmp.firstElementChild;
    const current = document.querySelector('#shopify-section-' + id);
    if (next && current) current.replaceWith(next);
  }

  // ── Open cart drawer from nav bag icon ──────────────────────────────────
  function bindCartIcon() {
    document.addEventListener('click', (e) => {
      const bag = e.target.closest('[data-kunu-cart-open]');
      if (!bag) return;
      e.preventDefault();
      const drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.open === 'function') {
        drawer.open(bag);
      } else {
        window.location.href = '/cart';
      }
    });
  }

  // ── Set initial max-height for any panel rendered in the open state ────
  function primeOpenPanels() {
    document.querySelectorAll('.kfaq__item[data-open="true"] .kfaq__panel, .pdp-collapse__row[data-open="true"] .pdp-collapse__panel').forEach((p) => {
      p.style.maxHeight = p.scrollHeight + 'px';
    });
  }

  // ── Init ────────────────────────────────────────────────────────────────
  function init() {
    bindStickyNav();
    bindMobileMenu();
    bindFaq();
    bindGallery();
    bindPdpCollapse();
    bindQty();
    bindAddToCart();
    bindCartIcon();
    measureFaqHeights();
    primeOpenPanels();
    window.addEventListener('resize', () => { measureFaqHeights(); primeOpenPanels(); });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
